import {
  ArrowRight,
  BarChart3,
  BriefcaseBusiness,
  Building2,
  CalendarDays,
  CalendarClock,
  Check,
  Clock3,
  FileText,
  Layers3,
  LockKeyhole,
  Mail,
  MapPin,
  MessageCircle,
  MessageSquareText,
  ShieldCheck,
  Smartphone,
  UserRoundCheck,
  Users,
} from "lucide-react";
import { Link } from "react-router-dom";

import PricingTable from "../components/PricingTable";
import Seo from "../components/Seo";
import { configValue } from "../config";
import { LEGAL_DOCUMENT_VERSION, legalCopy } from "../legalCopy";
import { formatDate } from "../utils/format";
import { normalizeWhatsAppPhoneNumber } from "../utils/whatsapp";

const faqItems = [
  [
    "Is DeVerlofkaart echt gratis voor persoonlijk gebruik?",
    "Ja. Free is bedoeld voor één gebruiker en bevat je persoonlijke saldi, jaarplanner, kalenderlagen, notities, PWA en export.",
  ],
  [
    "Kan ik mijn Excel vervangen?",
    "Ja. Je legt startsaldi, transport, ATV en je werkpatroon vast. Iedere mutatie blijft daarna herleidbaar.",
  ],
  [
    "Kan ik schoolvakanties toevoegen?",
    "Ja. Je kunt meerdere schoolkalenders toevoegen, bijvoorbeeld voor verschillende kinderen of scholen. Kies per kalender regio Noord, Midden of Zuid en voeg eigen vakanties en studiedagen toe. Deze dagen zijn informatief en kosten geen verlofuren.",
  ],
  [
    "Werkt DeVerlofkaart met ploegendiensten en onregelmatige roosters?",
    "Ja. Naast een vaste werkweek kun je 2-, 3- en 5-ploegendiensten of een eigen roterende cyclus vastleggen. Losse afwijkingen verwerk je per week, zodat een verlofdag volgens het werkelijk geplande aantal uren wordt berekend.",
  ],
  [
    "Kan mijn werkpatroon in de loop van het jaar veranderen?",
    "Ja. Werkpatronen hebben een ingangsdatum en blijven in de historie bewaard. Daardoor gebruikt een verlofberekening het rooster dat op de betreffende datum gold.",
  ],
  [
    "Kan ik mijn verlof in mijn eigen agenda bekijken?",
    "Ja. Maak een persoonlijke kalenderlink voor Apple Agenda, Google Agenda of Outlook en kies zelf welke soorten afspraken je deelt. Je kunt iedere link later intrekken of vervangen.",
  ],
  [
    "Kunnen medewerkers verlof aanvragen?",
    "Met Team of Business kunnen actieve organisatieleden verlof indienen op basis van hun werkpatroon en het organisatiebeleid.",
  ],
  [
    "Kan een manager aanvragen goedkeuren?",
    "Ja. Managers en bevoegde beheerders kunnen goedkeuren, afwijzen of om toelichting vragen. Zelfgoedkeuring wordt geblokkeerd.",
  ],
  [
    "Kan ik ATV bijhouden?",
    "Ja. ATV, regulier verlof en configureerbare verloftypen hebben ieder een eigen saldo en mutatiehistorie.",
  ],
  [
    "Werkt het op mijn telefoon?",
    "Ja. DeVerlofkaart is responsive en installeerbaar als PWA met een mobiele navigatie voor de belangrijkste flows.",
  ],
  [
    "Kan ik later upgraden?",
    "Ja. Je persoonlijke data blijft bestaan. Na een upgrade worden organisatie-entitlements server-side geactiveerd.",
  ],
];

function PageIntro({
  eyebrow,
  title,
  description,
}: {
  eyebrow: string;
  title: string;
  description: string;
}) {
  return (
    <section className="public-page-intro">
      <div className="marketing-container">
        <p className="eyebrow">{eyebrow}</p>
        <h1>{title}</h1>
        <p>{description}</p>
      </div>
    </section>
  );
}

export function PricingPage() {
  return (
    <>
      <Seo
        title="Prijzen"
        path="/prijzen"
        description="Vergelijk Free, Team en Business voor persoonlijke verlofplanning en teambeheer."
      />
      <PageIntro
        eyebrow="Prijzen"
        title="Een pakket dat met je verlofbeheer meegroeit."
        description="Begin gratis voor jezelf. Activeer Team of Business zodra medewerkers en fiattering nodig zijn."
      />
      <section className="marketing-band">
        <div className="marketing-container">
          <PricingTable />
          <p className="pricing-note">
            Alle bedragen en limieten worden centraal beheerd. Een betaling activeert
            pas functionaliteit nadat Mollie de status via een idempotente webhook heeft
            bevestigd.
          </p>
        </div>
      </section>
    </>
  );
}

export function FeaturesPage() {
  const groups = [
    [
      CalendarDays,
      "Persoonlijke planning",
      [
        "Actueel en beschikbaar saldo",
        "Jaarplanner in kalender en tabel",
        "Vaste en onregelmatige werkpatronen",
        "Saldo-opbouw en transport met vervaldatum",
      ],
    ],
    [
      Layers3,
      "Kalenderlagen",
      [
        "Meerdere schoolkalenders en regio's",
        "Eigen vakanties en studiedagen",
        "Nederlandse feestdagen",
        "Verjaardagen, afspraken en notities",
      ],
    ],
    [
      CalendarClock,
      "Roosters en contracten",
      [
        "2-, 3- en 5-ploegendiensten",
        "Eigen roterende cycli",
        "Afwijkende uren per werkweek",
        "Versies met een vaste ingangsdatum",
      ],
    ],
    [
      UserRoundCheck,
      "Aanvragen en fiattering",
      [
        "Hele en gedeeltelijke dagen",
        "Live urenberekening",
        "Goedkeuren, afwijzen, toelichting",
        "Traceerbare saldomutaties",
      ],
    ],
    [
      BarChart3,
      "Team en rapportage",
      [
        "Teamplanning en bezetting",
        "Medewerkers en managers",
        "Verlof per maand en type",
        "Kalender- of contractperioden",
      ],
    ],
    [
      Smartphone,
      "Mobiel en agenda",
      [
        "Installeerbaar op mobiel",
        "Touchvriendelijke navigatie",
        "Configureerbare kalenderfeeds",
        "Kalenderlinks intrekken of vervangen",
      ],
    ],
    [
      ShieldCheck,
      "Beveiliging",
      [
        "Server-side entitlements",
        "Organisaties strikt gescheiden",
        "Securityscans in CI",
        "Privacybewuste logging en analytics",
      ],
    ],
  ] as const;
  return (
    <>
      <Seo
        title="Functies"
        path="/functies"
        description="Bekijk alle functies voor verlofsaldi, jaarplanning, schoolvakanties, teams en fiattering."
      />
      <PageIntro
        eyebrow="Functies"
        title="Alles wat je nodig hebt om verlof voorspelbaar te maken."
        description="Uren zijn de primaire rekeneenheid. Informatieve kalenderlagen blijven altijd los van verlof en saldo."
      />
      <section className="marketing-band">
        <div className="marketing-container feature-detail-grid">
          {groups.map(([Icon, title, items]) => (
            <article key={title}>
              <Icon size={24} />
              <h2>{title}</h2>
              <ul>
                {items.map((item) => (
                  <li key={item}>
                    <Check size={16} />
                    {item}
                  </li>
                ))}
              </ul>
            </article>
          ))}
        </div>
      </section>
    </>
  );
}

export function EmployersPage() {
  return (
    <>
      <Seo
        title="Voor werkgevers"
        path="/voor-werkgevers"
        description="Beheer verlofaanvragen, fiattering, teambezetting en rapportages met DeVerlofkaart."
      />
      <PageIntro
        eyebrow="Voor werkgevers"
        title="Een werkbaar verlofproces voor kleine teams en MKB."
        description="Geen zwaar HR-systeem, wel duidelijke verantwoordelijkheden, een betrouwbare urenberekening en inzicht in bezetting."
      />
      <section className="marketing-band employer-flow">
        <div className="marketing-container">
          <div className="flow-steps">
            <article>
              <span>01</span>
              <Users size={23} />
              <h2>Nodig medewerkers uit</h2>
              <p>
                Koppel medewerkers via veilige e-mailuitnodigingen en wijs team- of
                managerrollen toe.
              </p>
            </article>
            <article>
              <span>02</span>
              <Clock3 size={23} />
              <h2>Bereken volgens hun rooster</h2>
              <p>
                Iedere aanvraag gebruikt de geldende werkpatroonversie, ook bij
                ploegendiensten, roterende cycli en afwijkende weekuren.
              </p>
            </article>
            <article>
              <span>03</span>
              <MessageSquareText size={23} />
              <h2>Beoordeel met context</h2>
              <p>
                Zie saldoimpact, meerdere schoolkalenders, collega-overlap en bezetting
                voordat je beslist.
              </p>
            </article>
            <article>
              <span>04</span>
              <BarChart3 size={23} />
              <h2>Rapporteer zonder giswerk</h2>
              <p>
                Volg gebruik, typen, openstaande aanvragen, contractperioden en
                vervallend transport per team.
              </p>
            </article>
          </div>
          <div className="employer-cta">
            <BriefcaseBusiness size={32} />
            <div>
              <h2>Klaar om één proces af te spreken?</h2>
              <p>Maak een account aan en kies Team tijdens de onboarding.</p>
            </div>
            <Link className="button primary" to="/registreren?plan=team">
              Start met Team
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}

export function FaqPage() {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    mainEntity: faqItems.map(([question, answer]) => ({
      "@type": "Question",
      name: question,
      acceptedAnswer: { "@type": "Answer", text: answer },
    })),
  };
  return (
    <>
      <Seo
        title="Veelgestelde vragen"
        path="/faq"
        description="Antwoorden over gratis gebruik, schoolvakanties, ATV, teams en de mobiele app."
        structuredData={structuredData}
      />
      <PageIntro
        eyebrow="FAQ"
        title="Veelgestelde vragen over DeVerlofkaart."
        description="Kort antwoord op de belangrijkste vragen voor persoonlijke gebruikers en werkgevers."
      />
      <section className="marketing-band">
        <div className="marketing-container faq-list">
          {faqItems.map(([question, answer]) => (
            <details key={question}>
              <summary>
                {question}
                <span>+</span>
              </summary>
              <p>{answer}</p>
            </details>
          ))}
        </div>
      </section>
    </>
  );
}

export function AboutPage() {
  return (
    <>
      <Seo
        title="Over ons"
        path="/over-ons"
        description="Waarom DeVerlofkaart verlofplanning overzichtelijk maakt voor mensen en kleine werkgevers."
      />
      <PageIntro
        eyebrow="Over DeVerlofkaart"
        title="Verlofbeheer dat begrijpelijk blijft."
        description="DeVerlofkaart is ontworpen als praktisch alternatief voor losse spreadsheets en versnipperde aanvraagprocessen."
      />
      <section className="marketing-band">
        <div className="marketing-container feature-detail-grid about-grid">
          <article>
            <Building2 size={24} />
            <h2>Voor echte werkweken</h2>
            <p>
              Uren, afwijkende werkdagen, carry-over en kalenderlagen zijn vanaf het
              datamodel leidend.
            </p>
          </article>
          <article>
            <ShieldCheck size={24} />
            <h2>Privacy als grens</h2>
            <p>
              Organisaties zijn strikt gescheiden en analytics ontvangt geen
              verlofredenen of medewerkersdetails.
            </p>
          </article>
          <article>
            <Users size={24} />
            <h2>Persoonlijk blijft persoonlijk</h2>
            <p>
              Je eigen verlofkaart blijft toegankelijk wanneer je lid wordt van een
              organisatie of een abonnement eindigt.
            </p>
          </article>
          <article>
            <Building2 size={24} />
            <h2>Ontwikkeld door AnchorWorks</h2>
            <p>
              DeVerlofkaart is een applicatie van AnchorWorks B.V. AnchorWorks
              ontwikkelt praktische software en helpt organisaties met IT-advies en
              beheer van digitale oplossingen.
            </p>
            <a
              className="text-link"
              href="https://anchorworks.nl"
              target="_blank"
              rel="noreferrer"
            >
              Bezoek AnchorWorks.nl <ArrowRight size={16} />
            </a>
          </article>
        </div>
      </section>
    </>
  );
}

export function ContactPage() {
  const contactEmail = configValue("CONTACT_EMAIL") || "info@deverlofkaart.nl";
  const whatsAppPhone = normalizeWhatsAppPhoneNumber(configValue("WHATSAPP_PHONE"));
  return (
    <>
      <Seo
        title="Contact"
        path="/contact"
        description="Neem contact op over DeVerlofkaart voor persoonlijk gebruik, teams of Business."
      />
      <PageIntro
        eyebrow="Contact"
        title="Bespreek je verlofproces."
        description="Neem per e-mail of WhatsApp contact op over de applicatie, een organisatie-inrichting of Business."
      />
      <section className="marketing-band">
        <div className="marketing-container">
          <div className="feature-detail-grid contact-options">
            <article>
              <Mail size={24} />
              <h2>E-mail</h2>
              <p>
                Vermeld geen medische informatie of andere gevoelige verlofdetails in je
                bericht.
              </p>
              <a className="text-link" href={`mailto:${contactEmail}`}>
                {contactEmail} <ArrowRight size={16} />
              </a>
            </article>
            <article>
              <MessageCircle size={24} />
              <h2>WhatsApp</h2>
              <p>Stel je vraag rechtstreeks aan DeVerlofkaart via WhatsApp.</p>
              {whatsAppPhone ? (
                <a
                  className="text-link"
                  href={`https://wa.me/${whatsAppPhone}`}
                  target="_blank"
                  rel="noreferrer"
                >
                  WhatsApp openen <ArrowRight size={16} />
                </a>
              ) : (
                <p>WhatsApp is tijdelijk niet beschikbaar.</p>
              )}
            </article>
            <article>
              <MapPin size={24} />
              <h2>Postadres</h2>
              <address>
                AnchorWorks B.V.
                <br />
                Binnendelta 11M
                <br />
                1261 WZ Blaricum
                <br />
                Nederland
              </address>
            </article>
          </div>
        </div>
      </section>
    </>
  );
}

export function LegalPage({ kind }: { kind: keyof typeof legalCopy }) {
  const copy = legalCopy[kind];
  const path = kind === "terms" ? "/algemene-voorwaarden" : `/${kind}`;
  const contactEmail = configValue("CONTACT_EMAIL") || "info@deverlofkaart.nl";
  return (
    <>
      <Seo title={copy.title} path={path} description={copy.intro} />
      <PageIntro eyebrow="Transparantie" title={copy.title} description={copy.intro} />
      <section className="marketing-band">
        <div className="marketing-container legal-copy">
          <div className="legal-status">
            <LockKeyhole size={20} />
            <p>
              Versie: {formatDate(LEGAL_DOCUMENT_VERSION)}. Van toepassing vanaf{" "}
              {formatDate(LEGAL_DOCUMENT_VERSION)}.
            </p>
          </div>
          {copy.sections.map(([title, text]) => (
            <section key={title}>
              <h2>{title}</h2>
              <p>{text}</p>
            </section>
          ))}
          <p className="legal-contact">
            <FileText size={18} /> Vragen:{" "}
            <a href={`mailto:${contactEmail}`}>{contactEmail}</a>
          </p>
        </div>
      </section>
    </>
  );
}
